import React, { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { API_ENDPOINTS } from '../../../constants/api';
import { worldCupQueryKeys } from '../../../src/worldcup/constants/worldcupQueryKeys';
import { isMemberRowQueryKey } from '../../../src/competitions/helpers/memberRowInvalidation.helper';
import { useWorldCupFuturesStats } from '../../../src/worldcup/hooks/useWorldCupFuturesStats';
import type {
  WorldCupFuturesOptions,
} from '../../../src/worldcup/helpers/worldcupPicksData.helper';
import type { FuturesPick } from '../../../src/worldcup/hooks/useWorldCupPicksData';
import { CompetitionFuturesView } from './CompetitionFuturesView';

interface CompetitionFuturesProps {
  competitionId: string;
  futures: FuturesPick | null;
  isEditable: boolean;
  futuresOptions: WorldCupFuturesOptions | null;
}

/**
 * Wired winner + top-scorer picks for a tournament competition
 * (specs/champions-league Story 15).
 *
 * The data is already on the screen: `GET /competitions/:id/matches` carries the options, the
 * viewer's pick and the lock, so this adds one mutation and the aggregate-percentage query and
 * nothing else. `useWorldCupFuturesStats` is named for the World Cup but its route has always
 * been `/:competitionId/futures-stats`; it takes the id it is given.
 */
export const CompetitionFutures: React.FC<CompetitionFuturesProps> = ({
  competitionId,
  futures,
  isEditable,
  futuresOptions,
}) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const futuresStats = useWorldCupFuturesStats(competitionId, competitionId !== '');

  const onSave = useCallback(
    async (
      winnerTeamId: string | null | undefined,
      topScorerPlayerId: string | null | undefined,
    ) => {
      // Send only the field being set. The route merges against what is stored, so a
      // scorer-only save must not carry a winner it would then re-validate.
      const data: Record<string, string> = {};
      if (winnerTeamId !== undefined && winnerTeamId !== null) data.winnerTeamId = winnerTeamId;
      if (topScorerPlayerId !== undefined && topScorerPlayerId !== null) {
        data.topScorerPlayerId = topScorerPlayerId;
      }
      if (Object.keys(data).length === 0) return;

      await apiClient(API_ENDPOINTS.competitions.futuresPath(competitionId), {
        method: 'PUT',
        data,
      });
      // The view already shows the new pick optimistically; re-read so a server-side
      // rejection or a concurrent change wins over the local guess.
      queryClient.invalidateQueries({ queryKey: worldCupQueryKeys.matchesMe(competitionId) });
      queryClient.invalidateQueries({ queryKey: worldCupQueryKeys.futuresStats(competitionId) });
      // The winner chip is drawn on every board too, from the member row rather than from this
      // screen's payload. Re-reading only the picks screen left the squad standings table
      // showing the previous pick until its 3-minute window expired or the user pulled to
      // refresh — which is how this was reported.
      queryClient.invalidateQueries({
        predicate: (query) => isMemberRowQueryKey(query.queryKey as readonly unknown[]),
      });
    },
    [apiClient, competitionId, queryClient],
  );

  return (
    <CompetitionFuturesView
      futures={futures}
      isEditable={isEditable}
      futuresOptions={futuresOptions}
      futuresStats={futuresStats}
      onSave={onSave}
    />
  );
};
